import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, ButtonProps, Text } from '@opendoor/bricks/core';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import ModalCard from '@opendoor/cinderblocks/growth/novo/modal/ModalCard';
import ModalHeader from '@opendoor/cinderblocks/growth/novo/modal/ModalHeader';

import { useLazyValidateEmailQuery, useSaveProgressMutation } from 'src/store/api';
import { useAppSelector } from 'src/store/hooks';
import { address as addressSelector } from 'src/store/sellerFlow/selectors';

import { getLeadToken } from '../../store/router/selectors';

const EmailInput = ({ isError, onEmailChange, email }) => (
  <Field
    errorText="Sorry, please check your email address and try again."
    label={'Email address'}
    sizeVariant={['200', null, '300']}
    input={{
      id: 'save-progress-email-input',
      error: isError,
      type: 'email',
      onChange: onEmailChange,
      onBlur: onEmailChange,
      value: email ?? '',
      name: 'save-progress-email-input',
      placeholder: 'you@example.com',
    }}
  />
);

const SaveProgress: React.FC = () => {
  const sellerInputUuid = useSelector(getLeadToken);
  const address = useAppSelector(addressSelector);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [validateEmail, emailValidationResult] = useLazyValidateEmailQuery();
  const [submitProgress, saveProgressResult] = useSaveProgressMutation();

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value ?? '';

    setEmail(newEmail);
    if (newEmail) {
      validateEmail(newEmail);
    }
  };

  const onEmailSubmit = () => {
    submitProgress({ email, sellerInputUuid });
  };

  let buttonProps: ButtonProps = {
    onClick: onEmailSubmit,
    loading: saveProgressResult.isLoading,
    disabled: !(email && emailValidationResult.data?.valid),
    analyticsName: 'reception-fe-save-progress-save-email',
    'aria-label': 'Save your progress',
    children: <>Save and continue</>,
  };

  if (saveProgressResult.isSuccess) {
    buttonProps = {
      variant: 'link',
      py: 6,
      width: '100%',
      textAlign: 'center',
      analyticsName: 'reception-fe-save-progress-email-close-modal',
      'aria-label': 'Close modal',
      onClick: () => setIsModalOpen(false),
      children: <>Your progress has been saved!</>,
    };
  }

  return (
    <>
      <Box>
        <Button
          px={1}
          variant="link"
          onClick={() => setIsModalOpen(true)}
          analyticsName="reception-fe-save-progress"
          aria-label="Save your progress"
          fontWeight="medium"
        >
          Save
        </Button>
      </Box>
      <ModalCard isOpen={isModalOpen} onModalClose={() => setIsModalOpen(false)}>
        <ModalHeader eyebrowText={address.street} headerText="Save your progress" />
        <Text
          fontSize={['14px', null, '16px']}
          lineHeight={['140', null, '130']}
          // @ts-expect-error letter spacing closer to design
          letterSpacing={['-0.196px', null, '-0.224px']}
          marginTop={['12px', null, '20px']}
        >
          We'll send you a link so you can pick up where you left off.
        </Text>
        <Box mt={['80px', null, '101px']}>
          <EmailInput
            isError={saveProgressResult.isError}
            onEmailChange={onEmailChange}
            email={email}
          />
          <Button mt={['24px']} {...buttonProps} />
        </Box>
      </ModalCard>
    </>
  );
};

export default SaveProgress;
